import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { SignedInAccount } from "../../../api/accountsApi";
import type { DeviceAuthorization } from "../../../api/connectorsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import type { UiError } from "../../../errors/uiError";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import AppButton from "../../../ui/buttons/AppButton";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import DefinitionListItem from "../../../ui/layout/DefinitionListItem";
import ConnectorDeviceDetails from "../ConnectorDeviceDetails";
import type { PairingDecision } from "./pairingFlow";

interface PairingRequestPanelProps {
  authorization: DeviceAuthorization;
  account: SignedInAccount;
  /** The decision being sent, if any. */
  pendingDecision: PairingDecision | null;
  decisionError: UiError | null;
  onDecide: (decision: PairingDecision) => void;
}

/**
 * A pending pairing: what is asking, for which account, on which server, since when; the
 * warning against approving someone else's code (RFC 8628 §5.4); approve or deny.
 */
export default function PairingRequestPanel({
  authorization,
  account,
  pendingDecision,
  decisionError,
  onDecide,
}: PairingRequestPanelProps) {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  const deciding = pendingDecision !== null;

  return (
    <section className="space-y-4" aria-labelledby={headingId}>
      <h2 id={headingId} className="app-title text-lg font-semibold">
        {t("connectors.pairing.requestTitle")}
      </h2>
      <ConnectorDeviceDetails
        device={authorization.device}
        leadingRows={
          <DefinitionListItem term={t("connectors.pairing.code")}>
            <span className="font-mono text-base font-semibold tracking-widest">{authorization.userCode}</span>
          </DefinitionListItem>
        }
        trailingRows={
          <>
            <DefinitionListItem term={t("connectors.pairing.requestedAt")}>
              {formatDateTime(authorization.createdAt, i18n.resolvedLanguage)}
            </DefinitionListItem>
            <DefinitionListItem term={t("connectors.pairing.expiresAt")}>
              {formatDateTime(authorization.expiresAt, i18n.resolvedLanguage)}
            </DefinitionListItem>
            <DefinitionListItem term={t("connectors.pairing.server")}>
              <span className="font-mono">{window.location.host}</span>
            </DefinitionListItem>
            <DefinitionListItem term={t("connectors.pairing.account")}>
              {t("connectors.pairing.accountValue", { displayName: account.displayName, email: account.email })}
            </DefinitionListItem>
          </>
        }
      />
      <p className="app-text-muted text-xs">{t("connectors.pairing.selfReported")}</p>

      <StatusMessage tone="warning" title={t("connectors.pairing.warningTitle")}>
        <p>{t("connectors.pairing.warningMessage", { userCode: authorization.userCode })}</p>
      </StatusMessage>

      {decisionError === null ? null : <ErrorNotice error={decisionError} />}

      <div className="flex flex-wrap gap-3">
        <AppButton
          type="button"
          variant="primary"
          size="sm"
          disabled={deciding}
          onClick={() => {
            onDecide("approve");
          }}
        >
          {pendingDecision === "approve" ? t("connectors.pairing.approving") : t("connectors.pairing.approve")}
        </AppButton>
        <AppButton
          type="button"
          variant="danger"
          size="sm"
          disabled={deciding}
          onClick={() => {
            onDecide("deny");
          }}
        >
          {pendingDecision === "deny" ? t("connectors.pairing.denying") : t("connectors.pairing.deny")}
        </AppButton>
      </div>
    </section>
  );
}
